import { useState } from "react";
import { useCreate, useGo, useSelect, useList, useCustom, useApiUrl } from "@refinedev/core";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { FormField, FormStickyActions, FormCard, FormHeader, FormSpacer } from "@/components/forms";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import type { SubscriptionCreatePayload, SubscriptionPlan } from "./types";
import type { PricingTier } from "@/resources/channel-passes/types";

export function SubscriptionsCreate() {
  const go = useGo();
  const apiUrl = useApiUrl();

  const [formData, setFormData] = useState<{
    userId: string;
    subscriptionPlanId: string;
    activateImmediately: boolean;
  }>({
    userId: "",
    subscriptionPlanId: "",
    activateImmediately: true,
  });

  // Load users
  const { options: userOptions, query: userQueryResult } = useSelect({
    resource: "users",
    optionLabel: "username",
    optionValue: "id",
    filters: [
      {
        field: "username",
        operator: "contains",
        value: "",
      },
    ],
  });

  // Load subscription plans
  const plansResult = useList<SubscriptionPlan>({
    resource: "subscription-plans",
    pagination: { mode: "off" },
    filters: [
      {
        field: "isActive",
        operator: "eq",
        value: true,
      },
    ],
  });

  const plans = plansResult.result?.data ?? [];
  const plansLoading = plansResult.query?.isLoading ?? false;

  const selectedPlanId = formData.subscriptionPlanId ? Number(formData.subscriptionPlanId) : null;
  const selectedPlan = plans.find((p) => p.id === selectedPlanId);

  // Load pricing tiers for selected plan
  const { query: tiersQuery } = useCustom<PricingTier[]>({
    url: `${apiUrl}/pricing-tiers/admin`,
    method: "get",
    config: {
      query: {
        subscriptionPlanId: selectedPlanId,
        isActive: true,
      },
    },
    queryOptions: {
      enabled: selectedPlanId !== null,
    },
  });

  const tiers: PricingTier[] = (() => {
    const raw = tiersQuery.data?.data;
    if (!raw) {
      return [];
    }
    if (Array.isArray(raw)) {
      return raw;
    }
    if (typeof raw === 'object' && 'data' in raw && Array.isArray((raw as { data: PricingTier[] }).data)) {
      return (raw as { data: PricingTier[] }).data;
    }
    return [];
  })();
  const tiersLoading = tiersQuery.isLoading;
  const hasTiers = tiers.length > 0;

  const createMutation = useCreate<SubscriptionCreatePayload>();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.userId || !formData.subscriptionPlanId) {
      return;
    }

    const payload: SubscriptionCreatePayload = {
      userId: Number(formData.userId),
      subscriptionPlanId: Number(formData.subscriptionPlanId),
      status: formData.activateImmediately ? "ACTIVE" : "PENDING",
    };

    createMutation.mutate(
      {
        resource: "subscriptions",
        values: payload,
      },
      {
        onSuccess: () => go({ to: "/subscriptions" }),
      }
    );
  };

  const isPending = createMutation.mutation?.isPending ?? false;

  const handlePlanChange = (val: string) => {
    setFormData({ ...formData, subscriptionPlanId: val });
  };

  const getTierName = (tier: PricingTier): string => {
    const translations = tier.translations;
    if (translations) {
      const ruName = translations.ru?.name;
      if (ruName) {
        return ruName;
      }
      for (const t of Object.values(translations)) {
        if (t?.name) { return t.name; }
      }
    }
    return `#${tier.id}`;
  };

  const formatTierPrice = (tier: PricingTier): string => {
    const { prices, baseCurrency } = tier;
    const basePrice = prices[baseCurrency];
    if (basePrice !== undefined) {
      return `${basePrice.toLocaleString()} ${baseCurrency}`;
    }
    const firstCurrency = Object.keys(prices)[0];
    if (firstCurrency) {
      return `${prices[firstCurrency].toLocaleString()} ${firstCurrency}`;
    }
    return '';
  };

  return (
    <div className="space-y-6">
      <FormHeader
        title="Новая подписка"
        description="Ручное добавление подписки пользователю. При активации пользователь получит доступ согласно entitlements плана."
        onBack={() => go({ to: "/subscriptions" })}
        sticky
      />

      <form id="subscription-create-form" onSubmit={handleSubmit} className="max-w-xl">
        <FormCard title="Детали подписки" description="Выберите пользователя и план подписки">
          <div className="space-y-4">
            <FormField label="Пользователь" name="userId" required>
              <Select
                value={String(formData.userId)}
                onValueChange={(val) =>
                  setFormData({ ...formData, userId: val })
                }
                disabled={isPending || userQueryResult.isLoading}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Выберите пользователя" />
                </SelectTrigger>
                <SelectContent>
                  {userOptions?.map((option) => (
                    <SelectItem key={option.value} value={String(option.value)}>
                      {option.label || `User #${option.value}`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FormField>

            <FormField label="Абонемент" name="subscriptionPlanId" required>
              <Select
                value={formData.subscriptionPlanId}
                onValueChange={handlePlanChange}
                disabled={isPending || plansLoading}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Выберите абонемент" />
                </SelectTrigger>
                <SelectContent>
                  {plans.map((plan) => (
                    <SelectItem key={plan.id} value={String(plan.id)}>
                      {plan.name || plan.code}
                      {plan.durationDays ? ` (${plan.durationDays} дней)` : ' (бессрочный)'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FormField>

            {selectedPlan && (
              <div className="rounded-md bg-muted p-3 text-sm space-y-1">
                <p className="font-medium">{selectedPlan.name || selectedPlan.code}</p>
                <p className="text-muted-foreground">
                  Длительность: {selectedPlan.durationDays ? `${selectedPlan.durationDays} дней` : 'бессрочный'}
                </p>
                {selectedPlan.price !== undefined && (
                  <p className="text-muted-foreground">
                    Цена (план): {selectedPlan.price} {selectedPlan.currency}
                  </p>
                )}
              </div>
            )}

            {selectedPlan && hasTiers && (
              <div className="rounded-md border p-3 space-y-2">
                <p className="text-sm font-medium">Тарифы абонемента</p>
                <div className="space-y-2">
                  {tiers.map((tier) => (
                    <div key={tier.id} className="flex items-center justify-between rounded-md bg-muted/50 p-2 text-sm">
                      <div className="space-y-0.5">
                        <p className="font-medium">{getTierName(tier)}</p>
                        <p className="text-muted-foreground">
                          {formatTierPrice(tier)}
                          {tier.durationDays ? ` / ${tier.durationDays} дней` : ''}
                        </p>
                      </div>
                      <div className="flex gap-1">
                        {tier.isFamily && (
                          <Badge variant="secondary">Семейный ({tier.familySlots})</Badge>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground">
                  Тарифы определяют цены для пользователей. Подписка создаётся на основе плана.
                </p>
              </div>
            )}

            {selectedPlan && !hasTiers && !tiersLoading && (
              <p className="text-xs text-muted-foreground">
                У абонемента нет тарифов. Подписка будет создана на основе настроек плана.
              </p>
            )}

            <div className="flex items-center space-x-2">
              <Switch
                id="activateImmediately"
                checked={formData.activateImmediately}
                onCheckedChange={(checked) =>
                  setFormData({ ...formData, activateImmediately: checked })
                }
                disabled={isPending}
              />
              <Label htmlFor="activateImmediately" className="cursor-pointer">
                Активировать сразу
              </Label>
            </div>
            <p className="text-xs text-muted-foreground">
              При активации пользователь автоматически получит доступ к курсам и каналам
              согласно entitlements плана подписки.
            </p>
          </div>
        </FormCard>

      </form>

      <FormSpacer />

      <FormStickyActions>
        <Button type="submit" form="subscription-create-form" disabled={isPending || !formData.userId || !formData.subscriptionPlanId}>
          Создать подписку
        </Button>
        <Button
          type="button"
          variant="outline"
          onClick={() => go({ to: "/subscriptions" })}
          disabled={isPending}
        >
          Отмена
        </Button>
      </FormStickyActions>
    </div>
  );
}
